import React, { useState, useEffect, useRef } from 'react';
import { hospitalApi, contractApi } from '../services/api';

interface Hospital {
  hospitalId: string;
  name: string;
}

interface Contract {
  key: string;
  contractType: string;
  year: string;
  month: string;
  fileName: string;
  size?: number;
  lastModified?: string;
}

const CONTRACT_TYPES = ['subscription', 'device', 'other'] as const;
type ContractType = typeof CONTRACT_TYPES[number];

const Contracts: React.FC = () => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [contractType, setContractType] = useState<ContractType>('subscription');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    hospitalApi.getAll().then(setHospitals);
  }, []);

  useEffect(() => {
    if (!selectedHospitalId) {
      setContracts([]);
      return;
    }
    const hospital = hospitals.find(h => h.hospitalId === selectedHospitalId);
    loadContracts(selectedHospitalId, hospital?.name);
  }, [selectedHospitalId]);

  const loadContracts = async (hospitalId: string, hospitalName?: string) => {
    setLoading(true);
    try {
      const result = await contractApi.listByHospital(hospitalId, hospitalName);
      const flat: Contract[] = [];
      if (result.contracts) {
        Object.values(result.contracts).forEach((typeGroup: any) => {
          if (Array.isArray(typeGroup)) {
            flat.push(...typeGroup);
          } else if (typeof typeGroup === 'object') {
            Object.values(typeGroup).forEach((items: any) => {
              if (Array.isArray(items)) flat.push(...items);
            });
          }
        });
      }
      setContracts(flat.sort((a, b) => (b.lastModified || '').localeCompare(a.lastModified || '')));
    } catch (err) {
      setError('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedHospitalId) return;
    const hospital = hospitals.find(h => h.hospitalId === selectedHospitalId);
    if (!hospital) return;

    setUploading(true);
    setError(null);
    try {
      const result = await contractApi.getUploadUrl({
        hospitalId: selectedHospitalId,
        hospitalName: hospital.name,
        contractType,
        fileName: selectedFile.name,
        contentType: selectedFile.type || 'application/octet-stream',
      });

      // Upload directly to S3 via presigned URL
      const uploadResponse = await fetch(result.uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: { 'Content-Type': selectedFile.type || 'application/octet-stream' },
      });

      if (!uploadResponse.ok) throw new Error('Upload to S3 failed');

      setSuccessMsg(`${selectedFile.name} uploaded successfully`);
      setShowUploadModal(false);
      setSelectedFile(null);
      setContractType('subscription');
      setTimeout(() => setSuccessMsg(null), 3000);
      await loadContracts(selectedHospitalId, hospital.name);
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (s3Key: string) => {
    try {
      const result = await contractApi.getDownloadUrl(s3Key);
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setError('Failed to get download link');
    }
  };

  const handleDelete = async (s3Key: string, fileName: string) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      await contractApi.delete(s3Key);
      const hospital = hospitals.find(h => h.hospitalId === selectedHospitalId);
      await loadContracts(selectedHospitalId, hospital?.name);
    } catch (err) {
      setError('Failed to delete contract');
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Contracts</h2>
        {selectedHospitalId && (
          <button className="btn-primary" onClick={() => setShowUploadModal(true)}>
            Upload Contract
          </button>
        )}
      </div>

      {error && <div className="error-banner" onClick={() => setError(null)}>{error} ×</div>}
      {successMsg && <div className="success-banner">{successMsg}</div>}

      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label>Select Hospital</label>
        <select
          value={selectedHospitalId}
          onChange={e => setSelectedHospitalId(e.target.value)}
          className="form-control"
        >
          <option value="">— choose a hospital —</option>
          {hospitals.filter(h => (h as any).isActive !== false).map(h => (
            <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>
          ))}
        </select>
      </div>

      {loading && <p>Loading contracts...</p>}

      {!loading && selectedHospitalId && contracts.length === 0 && (
        <p style={{ color: '#666' }}>No contracts uploaded yet for this hospital.</p>
      )}

      {contracts.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Type</th>
              <th>Date</th>
              <th>Size</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map(c => (
              <tr key={c.key}>
                <td>{c.fileName}</td>
                <td style={{ textTransform: 'capitalize' }}>{c.contractType}</td>
                <td>{formatDate(c.lastModified)}</td>
                <td>{formatSize(c.size)}</td>
                <td>
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => handleDownload(c.key)}
                    style={{ marginRight: '8px' }}
                  >
                    Download
                  </button>
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDelete(c.key, c.fileName)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Contract</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Contract Type</label>
                <select
                  value={contractType}
                  onChange={e => setContractType(e.target.value as ContractType)}
                  className="form-control"
                >
                  {CONTRACT_TYPES.map(t => (
                    <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                />
                {selectedFile && <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{selectedFile.name} ({formatSize(selectedFile.size)})</p>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
